/**
 * AI督导相关API
 */

import { request, BaseResponse } from '../../api';

// 督导场景信息
export interface SupervisorScenarioInfo {
  id: string;
  name: string;
  description: string;
  key_points: string;
  audio_video_source: string;
  device_type: string | null;
  passing_score: number;
  status: number;
  created_at: string;
  updated_at: string;
  max_upload_limit:number;
  practice_count?: number; // 用户在该场景下的练习次数
}

// 获取我的督导场景列表的参数
export interface GetMySupervisorScenariosParams {
  page?: number;
  pageSize?: number;
}

// 获取我的督导场景列表的响应
export interface GetMySupervisorScenariosResponse {
  list: SupervisorScenarioInfo[];
  total: number;
  current: number;
  pageSize: number;
}

/**
 * 获取我的督导场景列表
 */
export const getMySupervisorScenarios = async (
  params: GetMySupervisorScenariosParams = {}
): Promise<BaseResponse<GetMySupervisorScenariosResponse>> => {
  const { page = 1, pageSize = 20 } = params;
  
  return request<GetMySupervisorScenariosResponse>('/supervisor-assigns/my-scenarios', {
    method: 'GET',
    params: {
      page,
      pageSize
    }
  });
};

/**
 * 获取督导场景详情
 */
export const getSupervisorScenarioDetail = async (
  scenarioId: string
): Promise<BaseResponse<SupervisorScenarioInfo>> => {
  return request<SupervisorScenarioInfo>(`/supervisor-scenarios/${scenarioId}`, {
    method: 'GET'
  });
};

// 督导分析详情（来自后端API）
export interface SupervisorAnalysisDetail {
  id: string;
  content_id: string;
  scenario_id: string;
  user_id: string;
  user_name: string | null;
  analysis_result: {
    overall_score: number;
    dimension_scores: Record<string, {
      score: number;
      total: number;
      feedback: string;
    }>;
    comprehensive_feedback: string;
    improvement_suggestions: string[];
    evaluation_details: {
      strengths: string[];
      weaknesses: string[];
      key_moments: string[];
    };
  };
  overall_score: number;
  passed_status: string;
  duration: number | null;
  scenario_data: {
    name: string;
    description?: string;
    key_points?: string;
    scoring_criteria_config: {
      criteria_data: {
        dimensions: Array<{
          name: string;
          weight: number;
          description?: string;
          scoring_rule?: string;
        }>;
      };
    };
    passing_score: number;
  } | null;
  content_data: {
    id: string;
    file_name: string | null;
    content_type: 'audio' | 'video';
    source_url: string | null;
    duration: number | null;
    file_size: number | null;
  } | null;
  created_at: string;
}

// 用于展示的分析详情（适配后的格式）
export interface DisplayAnalysisDetail {
  session_scores: {
    overall_score: number;
    feedback: string;
    dimension_scores: Record<string, {
      score: number;
      total: number;
      feedback: string;
    }>;
  };
  scenario_data: {
    id: string;
    name: string;
    passing_score: number;
    scoring_criteria_config: {
      criteria_data: {
        dimensions: Array<{
          name: string;
          scoring_rule: string;
        }>;
      };
    };
  };
  duration: number;
  start_time: string;
  user_name: string;
  avatar: string | null;
  messages: Array<{
    ai_message: {
      content: string;
    };
    user_message: {
      content: string;
      scores: {
        user_message_analysis: {
          score: number;
          suggestions: string;
          golden_responses: string[];
        };
      };
    };
  }>;
}

// 督导内容信息
export interface SupervisorContentInfo {
  id: string;
  user_id: string;
  user_name?: string;
  content_type: 'audio' | 'video';
  file_name: string;
  source_url: string;
  parse_status: 'pending' | 'processing' | 'completed' | 'failed';
  asr_status: 'pending' | 'processing' | 'completed' | 'failed';
  duration: number | null;
  file_size: number | null;
  created_at: string;
  scenario_count: number;
  analysis_count: number;
  latest_score: number | null;
}

// 创建督导内容参数
export interface CreateSupervisorContentParams {
  scenario_ids: string[]; // 关联的督导场景ID列表
  content_type: 'audio' | 'video'; // 只支持音视频
  source_url: string; // 文件URL（阿里云OSS）
  file_name: string; // 文件名
  file_size: number; // 文件大小（字节）
}

// 创建督导内容响应
export interface CreateSupervisorContentResponse {
  content_id: string;
  message: string;
}

// 获取督导内容列表参数
export interface GetSupervisorContentsParams {
  scenario_id?: string;
  page?: number;
  pageSize?: number;
}

// 查询内容处理状态响应
export interface SupervisorContentStatusResponse {
  content_id: string;
  asr_status: 'pending' | 'processing' | 'completed' | 'failed';
  parse_status: 'pending' | 'processing' | 'completed' | 'failed';
  parse_error: string | null;
  raw_content: string | null;
  parsed_content: string | null;
  duration: number | null;
  source_url: string;
  updated_at: string;
}

// 获取督导内容列表响应
export interface GetSupervisorContentsResponse {
  list: SupervisorContentInfo[];
  total: number;
  current: number;
  pageSize: number;
}

// 督导内容详情
export interface SupervisorContentDetail {
  id: string;
  user_id: string;
  user_name?: string;
  content_type: 'audio' | 'video';
  file_name: string;
  source_url: string;
  raw_content: string | null;
  parsed_content: string | null;
  parse_status: 'pending' | 'processing' | 'completed' | 'failed';
  asr_status: 'pending' | 'processing' | 'completed' | 'failed';
  parse_error: string | null;
  duration: number | null;
  file_size: number | null;
  created_at: string;
  updated_at: string;
  scenario_count?: number;
  analyses?: Array<{
    id: string;
    overall_score: number;
    passed_status: string;
    created_at: string;
  }>;
}

/**
 * 创建督导内容（上传文件）
 */
export const createSupervisorContent = async (
  params: CreateSupervisorContentParams
): Promise<BaseResponse<CreateSupervisorContentResponse>> => {
  return request<CreateSupervisorContentResponse>('/supervisor-contents', {
    method: 'POST',
    data: params
  });
};

/**
 * 查询督导内容处理状态
 */
export const getSupervisorContentStatus = async (
  contentId: string
): Promise<BaseResponse<SupervisorContentStatusResponse>> => {
  return request<SupervisorContentStatusResponse>(`/supervisor-contents/${contentId}/status`, {
    method: 'GET'
  });
};

/**
 * 获取督导内容列表
 */
export const getSupervisorContents = async (
  params: GetSupervisorContentsParams = {}
): Promise<BaseResponse<GetSupervisorContentsResponse>> => {
  const { page = 1, pageSize = 20, scenario_id } = params;
  
  return request<GetSupervisorContentsResponse>('/supervisor-contents', {
    method: 'GET',
    params: {
      page,
      pageSize,
      scenario_id
    }
  });
};

/**
 * 获取督导内容详情
 */
export const getSupervisorContentDetail = async (
  contentId: string
): Promise<BaseResponse<SupervisorContentDetail>> => {
  return request<SupervisorContentDetail>(`/supervisor-contents/${contentId}`, {
    method: 'GET'
  });
};

/**
 * 生成督导分析
 */
export const generateSupervisorAnalysis = async (
  contentId: string,
  scenarioId: string
): Promise<BaseResponse<SupervisorAnalysisDetail>> => {
  const response = await request<SupervisorAnalysisDetail>('/supervisor-analyses/generate', {
    method: 'POST',
    data: {
      content_id: contentId,
      scenario_id: scenarioId
    }
  });
  return response;
};

/**
 * 获取督导分析详情
 */
export const getSupervisorAnalysisDetail = async (
  analysisId: string
): Promise<BaseResponse<SupervisorAnalysisDetail>> => {
  return request<SupervisorAnalysisDetail>(`/supervisor-analyses/${analysisId}`, {
    method: 'GET'
  });
};

/**
 * 通过fileId查询分析结果（钉钉适配）
 * @param fileId 文件ID
 * @param scenarioId 场景ID（可选，如果不传则查询最新的分析结果）
 */
export const getSupervisorAnalysisByFileId = async (
  fileId: string,
  scenarioId?: string
): Promise<BaseResponse<SupervisorAnalysisDetail | null>> => {
  // 如果没有传 scenarioId，使用 getLastAnalysisByFileId 接口
  if (!scenarioId) {
    return request<SupervisorAnalysisDetail | null>('/supervisor-analyses/getLastAnalysisByFileId', {
      method: 'GET',
      params: {
        file_id: fileId
      }
    });
  }

  // 如果传了 scenarioId，使用 getAoneResult 接口
  return request<SupervisorAnalysisDetail | null>('/supervisor-analyses/getAoneResult', {
    method: 'GET',
    params: {
      file_id: fileId,
      scenario_id: scenarioId
    }
  });
};

/**
 * 钉钉适配 - 选择场景并开始分析
 */
export interface ChooseScenarioParams {
  agentId: string;
  corpId: string;
  userId: string;
  fileId: string;
  instanceId: string;
  scenario_id: string;
  union_id?: string;
}

export const chooseScenario = async (
  params: ChooseScenarioParams
): Promise<BaseResponse<{ content_id: string; message: string }>> => {
  return request<{ content_id: string; message: string }>('/supervisor-contents/choose_scenario', {
    method: 'POST',
    data: params
  });
};

/**
 * 查询用户上传次数
 */
export interface UploadCountResponse {
  scenario_id: string;
  upload_count: number;
  max_upload_limit: number | null;
  can_upload: boolean;
  remaining_count: number | null;
}

export const getUploadCount = async (
  scenarioId: string
): Promise<BaseResponse<UploadCountResponse>> => {
  return request<UploadCountResponse>(`/supervisor-contents/upload-count/${scenarioId}`, {
    method: 'GET'
  });
};

/**
 * 获取督导分析详情（模拟数据版本，用于测试）
 */
export const getSupervisorAnalysisDetailMock = async (
  sessionId: string
): Promise<BaseResponse<DisplayAnalysisDetail>> => {
  // 这里暂时返回模拟数据
  
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        success: true,
        data: {
          session_scores: {
            overall_score: 85,
            feedback: '从对话内容来看，该员工对宝马车辆从对话内容来看，该员工对宝马车辆从对话内容来看，该员工对宝马车辆从对话内容来看，该员工对宝马车辆',
            dimension_scores: {
              '开场与自我介绍': {
                score: 20,
                total: 100,
                feedback: '开场较为生硬，缺乏亲和力和专业性。建议优化问候语，增加微笑和眼神交流。'
              },
              '需求挖掘': {
                score: 20,
                total: 100,
                feedback: '对客户需求挖掘不够深入，未能有效引导客户表达真实需求。'
              },
              '产品介绍': {
                score: 20,
                total: 100,
                feedback: '产品介绍较为全面，但缺乏针对性，未能根据客户需求进行个性化推荐。'
              },
              '后续行动计划': {
                score: 20,
                total: 100,
                feedback: '后续跟进计划明确，但缺乏具体时间节点和行动步骤。'
              },
              '完整度': {
                score: 20,
                total: 100,
                feedback: '对话流程完整，各环节衔接自然，但部分细节处理有待提升。'
              }
            }
          },
          scenario_data: {
            id: sessionId || 'mock-scenario-id',
            name: '2025年Q1销售实战抽查抽查抽查',
            passing_score: 80,
            scoring_criteria_config: {
              criteria_data: {
                dimensions: [
                  { name: '开场与自我介绍', scoring_rule: '标准话术熟练度、禁止与违规遵守' },
                  { name: '需求挖掘', scoring_rule: '同理心、解决方案准确性及响应速度' },
                  { name: '产品介绍', scoring_rule: '回理心、解决方案准确性及响应应速度' },
                  { name: '后续行动计划', scoring_rule: '回理心、解决方案准确性及响应应速度' },
                  { name: '完整度', scoring_rule: '回理心、解决方案准确性及响应应速度' }
                ]
              }
            }
          },
          duration: 840,
          start_time: '2023-11-12T11:00:21Z',
          user_name: '待验证',
          avatar: null,
          messages: [
            {
              ai_message: {
                content: '您好，请问有什么可以帮助您的吗？'
              },
              user_message: {
                content: '我想了解一下贵公司的产品。',
                scores: {
                  user_message_analysis: {
                    score: 75,
                    suggestions: '回答较为简洁，建议可以更详细地表达需求，以便获得更准确的产品推荐。',
                    golden_responses: ['您好，我对贵公司的XX产品很感兴趣，能否详细介绍一下产品的功能和优势？']
                  }
                }
              }
            },
            {
              ai_message: {
                content: '当然可以，我们有多款优秀的产品，请问您具体对哪个方面比较感兴趣？'
              },
              user_message: {
                content: '我想了解一下价格方面的信息。',
                scores: {
                  user_message_analysis: {
                    score: 80,
                    suggestions: '提问较为直接，建议先了解产品功能后再询问价格，这样更有助于做出理性决策。',
                    golden_responses: ['在了解产品功能和适用场景后，我想进一步了解价格信息，以便评估是否符合我们的预算。']
                  }
                }
              }
            },
            {
              ai_message: {
                content: '关于价格，我们有多种套餐可供选择，请问您的预算范围是多少？'
              },
              user_message: {
                content: '预算在10万元左右。',
                scores: {
                  user_message_analysis: {
                    score: 85,
                    suggestions: '预算表述清晰，有助于销售人员提供更精准的方案。',
                    golden_responses: ['我们的预算在10万元左右，希望能在这个范围内找到性价比最高的产品方案。']
                  }
                }
              }
            }
          ]
        } as DisplayAnalysisDetail
      });
    }, 2000);
  });
};

